import React from 'react'
import './MessageCenterFaxPreviewStyle.css'
import useFaxPreview from './useFaxPreview'
import { EmptyState, Modal } from '@components/ui'
import { AddContactModal } from '@components/layout/Modals'
import MessageCenterDeleteFaxModal from '../../MessageCenterDeleteFaxModal.component'
import { properNoun } from '@utils/fax.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { setActiveFax, setActiveFolder } from '@store/reducers/fax.reducer'
import FaxListToolbar from './FaxListToolbar.component'
import FaxMessageList from './FaxMessageList.component'
import FaxDetailPreview from './FaxDetailPreview.component'
import FaxHistoryTable from './FaxHistoryTable.component'
import FaxBlockedTable from './FaxBlockedTable.component'
import FaxBlockNumberModal from './modals/FaxBlockNumberModal.component'

/** Which "nothing to show" state the list renders. */
type FaxEmptyVariant = 'results' | 'blocked' | 'history' | 'folder'

const FAX_EMPTY_STATES: Record<
  FaxEmptyVariant,
  { title: string; description: string; iconClass: string }
> = {
  results: {
    title: 'No faxes found',
    description: 'No faxes match your search.',
    iconClass: 'mgc mgc_fax_fill',
  },
  blocked: {
    title: 'No blocked fax numbers found',
    description: "You don't have any blocked fax numbers on record yet.",
    iconClass: 'mgc mgc_fax_fill',
  },
  history: {
    title: 'No faxes found',
    description: "You don't have any faxes on record yet.",
    iconClass: 'mgc mgc_fax_fill',
  },
  folder: {
    title: 'No faxes found',
    description: 'There are no faxes in this folder yet.',
    iconClass: 'mgc mgc_fax_fill',
  },
}

const FaxPreview: React.FC = () => {
  const hooks = useFaxPreview()
  const dispatch = useAppDispatch()
  const folders = useAppSelector((state) => state.fax.folders)
  const faxSearchQuery = useAppSelector((state) => state.fax.searchQuery)
  const faxFilters = useAppSelector((state) => state.fax.faxFilters)
  const { listViewMode } = hooks

  const hasListContent =
    listViewMode === 'blocked'
      ? hooks.filteredBlockedUsers.length > 0
      : hooks.listMessages.length > 0

  /** True when a search term or filter is narrowing the list, so "no results" reads correctly. */
  const listIsNarrowed =
    faxSearchQuery.trim().length > 0 ||
    Object.values(faxFilters ?? {}).some((values) => (values?.length ?? 0) > 0)

  const emptyVariant: FaxEmptyVariant = listIsNarrowed
    ? 'results'
    : listViewMode === 'blocked'
      ? 'blocked'
      : listViewMode === 'history'
        ? 'history'
        : 'folder'

  const emptyState = FAX_EMPTY_STATES[emptyVariant]

  return (
    <>
      <div className="fax-preview-container">
        {hasListContent ? (
          hooks.selectedFax ? (
            <FaxDetailPreview
              fax={hooks.selectedFax}
              hooks={hooks}
              folders={folders}
              tags={hooks.tags}
            />
          ) : (
            <>
              <FaxListToolbar
                mode="list"
                hooks={hooks}
                folders={folders}
                tags={hooks.tags}
              />
              {listViewMode === 'blocked' ? (
                <FaxBlockedTable hooks={hooks} />
              ) : listViewMode === 'history' ? (
                <FaxHistoryTable hooks={hooks} />
              ) : (
                <FaxMessageList hooks={hooks} />
              )}
            </>
          )
        ) : (
          <EmptyState
            className="mc-fax-pane-empty"
            iconClass={emptyState.iconClass}
            title={emptyState.title}
            description={emptyState.description}
            actionLabel={emptyVariant === 'folder' ? 'Send fax' : undefined}
            onAction={
              emptyVariant === 'folder'
                ? () => {
                    dispatch(setActiveFax(null))
                    dispatch(setActiveFolder('compose'))
                  }
                : undefined
            }
          />
        )}
      </div>
      {hooks.confirmModalState?.action === 'delete' ||
      hooks.confirmModalState?.action === 'permanently_delete' ? (
        <MessageCenterDeleteFaxModal
          show={hooks.confirmModalState.show}
          count={hooks.confirmModalState._selectedFaxes?.length ?? 0}
          permanent={hooks.confirmModalState.action === 'permanently_delete'}
          onCancel={hooks.onHideConfirmModal}
          onDelete={hooks.onConfirmActionButton}
        />
      ) : hooks.confirmModalState ? (
        <Modal
          open={hooks.confirmModalState.show}
          onClose={hooks.onHideConfirmModal}
          size="xs"
          title={hooks.confirmModalState.mainText}
          description={hooks.confirmModalState.subText}
          primaryLabel={properNoun(
            hooks.confirmModalState.action.replace('_', ' '),
          )}
          secondaryLabel="Cancel"
          primaryDestructive={hooks.confirmModalState.action.includes('delete')}
          onPrimaryClick={hooks.onConfirmActionButton}
          onSecondaryClick={hooks.onHideConfirmModal}
        />
      ) : null}
      {hooks.showAddContactModal && (
        <AddContactModal
          show={hooks.showAddContactModal}
          onHide={hooks.onHideAddContactModal}
          initialContactData={{
            clinic_id: '',
            title: 'Mr.',
            first_name: '',
            last_name: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            email: '',
            cellphone: '',
            fax_number: hooks.addContactInitialFaxNumber,
          }}
        />
      )}
      {hooks.showBlockModal && (
        <FaxBlockNumberModal
          show={hooks.showBlockModal}
          onHide={hooks.onHideBlockModal}
          onSuccess={
            hooks.isDetailView ? hooks.handleExitDetailAfterAction : undefined
          }
          phoneNumbers={hooks.blockModalPhones}
          contactId={hooks.blockModalContactId}
          existingBlockedUsers={hooks.existingBlockedUsers}
        />
      )}
    </>
  )
}

export default FaxPreview
